class Toast extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.autoHideTimer = null;
  }

  connectedCallback() {
    this.render();

    // Auto-show if data-auto-show is present
    if (this.hasAttribute('data-auto-show')) {
      setTimeout(() => this.show(), 100);
    }
  }

  static get observedAttributes() {
    return [
      'data-bg-style',
      'data-header',
      'data-description',
      'data-emoji',
      'data-count',
      'data-pointer',
      'data-duration',
      'data-link1',
      'data-link1-url',
      'data-image', // Added data-image to observed attributes
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Re-render if any observed attribute changes
    if (this.isConnected && oldValue !== newValue) {
      this.render();
    }
  }

  render() {
    // Clear existing content to prevent duplication on re-render
    this.template = this.querySelector('template');
    this.innerHTML = this.template ? this.template.innerHTML : '';

    if (this.template) {
      return;
    }

    const bgStyle = this.getAttribute('data-bg-style') || 'basic';
    const header = this.getAttribute('data-header');
    const description = this.getAttribute('data-description');
    const emoji = this.getAttribute('data-emoji');
    const image = this.getAttribute('data-image');
    const pointer = this.getAttribute('data-pointer') || 'top';
    const count = this.getAttribute('data-count');
    const link1 = this.getAttribute('data-link1');
    const link1Url = this.getAttribute('data-link1-url');

    const toastWrapper = document.createElement('div');
    toastWrapper.className = 'toast-wrapper';

    const toast = document.createElement('div');
    toast.className = `toast toast-bg-style-${bgStyle} toast-pointer-${pointer}`;

    // Image/Emoji section
    if (image || emoji) {
      const toastImageDiv = document.createElement('div');
      toastImageDiv.className = 'toast-image';
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = header || description || '';
        toastImageDiv.appendChild(img);
        toast.classList.add('toast-has-image');
      } else if (emoji) {
        toastImageDiv.classList.add('toast-image-emoji');
        const span = document.createElement('span');
        span.className = 'fixed-body-m';
        span.textContent = emoji;
        toastImageDiv.appendChild(span);
        toast.classList.add('toast-emoji');
      }
      toast.appendChild(toastImageDiv);
    }

    // Content section
    const toastContent = document.createElement('div');
    toastContent.className = 'toast-content';

    if (header) {
      const toastHeader = document.createElement('div');
      toastHeader.className = 'toast-header';
      const span = document.createElement('span');
      span.className = 'fixed-utility-s-em';
      span.textContent = header;
      toastHeader.appendChild(span);
      toastContent.appendChild(toastHeader);
    }

    const toastContentBody = document.createElement('div');
    toastContentBody.className = 'toast-content-body';

    const toastCopy = document.createElement('div');
    toastCopy.className = 'toast-copy';

    if (count) {
      const countSpan = document.createElement('span');
      countSpan.className = 'fixed-body-s-em';
      countSpan.textContent = count;
      toastCopy.appendChild(countSpan);
    }

    if (description) {
      const descSpan = document.createElement('span');
      descSpan.className = 'fixed-body-s';
      descSpan.textContent = description;
      toastCopy.appendChild(descSpan);
    }
    toastContentBody.appendChild(toastCopy);

    // Link section (simplified for now)
    if (link1 && link1Url) {
      const link = document.createElement('a');
      link.href = link1Url;
      link.textContent = link1;
      link.className = 'fixed-utility-s-em text_link'; // Add text_link class for styling
      toastContentBody.appendChild(link);
    }

    toastContent.appendChild(toastContentBody);
    toast.appendChild(toastContent);

    // Progress bar
    const progressBar = document.createElement('div');
    progressBar.className = 'toast-progress-bar';
    toast.appendChild(progressBar);

    toastWrapper.appendChild(toast);
    this.appendChild(toastWrapper);
  }

  show(duration) {
    this.isVisible = true;
    this.classList.add('show');
    this.style.display = 'block';

    const autoDuration = duration || parseInt(this.getAttribute('data-duration')) || 5000;

    // Start progress bar animation
    const progressBar = this.querySelector('.toast-progress-bar');
    if (progressBar) {
      progressBar.style.transform = 'scaleX(0)';
      progressBar.style.transition = `transform ${autoDuration}ms linear`;

      // Trigger animation on next frame
      requestAnimationFrame(() => {
        progressBar.style.transform = 'scaleX(1)';
      });
    }

    // Auto hide
    if (autoDuration > 0) {
      this.autoHideTimer = setTimeout(() => {
        this.hide();
      }, autoDuration);
    }

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent('toast-show', {
        bubbles: true,
        detail: { toast: this },
      }),
    );
  }

  hide() {
    this.isVisible = false;
    this.classList.remove('show');
    this.style.display = 'none';

    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer);
      this.autoHideTimer = null;
    }

    // Remove from DOM after animation
    setTimeout(() => {
      if (this.parentNode && !this.isVisible) {
        this.style.display = 'none';
      }
    }, 300);

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent('toast-hide', {
        bubbles: true,
        detail: { toast: this },
      }),
    );
  }

  static show(options = {}) {
    const toast = new Toast();
    toast.show(options.duration);
    return toast;
  }
}

// Register the custom element
customElements.define('toast-notification', Toast);

class ToastManager {
  constructor() {
    if (ToastManager.instance) {
      return ToastManager.instance;
    }

    this.toastQueue = [];
    this.activeToasts = [];
    this.maxActiveToasts = 3; // Example: maximum 3 toasts visible at once

    ToastManager.instance = this;
  }

  /**
   * Adds a new toast to the queue and attempts to display it.
   * @param {object} options - Options for the toast (e.g., header, description, duration, target).
   *                           These will be set as data attributes on the toast-notification element.
   *                           `target` should be a DOM element where the toast will be appended.
   */
  addToast(options) {
    const toastElement = document.createElement('toast-notification');

    // Set data attributes from options
    for (const key in options) {
      if (Object.prototype.hasOwnProperty.call(options, key) && key !== 'target') {
        // Exclude target from data attributes
        // Convert camelCase to kebab-case for data attributes
        const dataAttr = `data-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        toastElement.setAttribute(dataAttr, options[key]);
      }
    }

    // Store the target element with the toast in the queue
    this.toastQueue.push({ toastElement, target: options.target || document.body });
    this.processQueue();
  }

  processQueue() {
    while (this.toastQueue.length > 0 && this.activeToasts.length < this.maxActiveToasts) {
      const { toastElement, target } = this.toastQueue.shift();
      this.activeToasts.push(toastElement);
      target.appendChild(toastElement); // Append to the specified target
      // Call the show method on the custom element
      // The duration can be passed here or read from data-duration attribute
      toastElement.show(toastElement.getAttribute('data-duration'));
    }
  }

  onToastHidden(hiddenToast) {
    // Remove the toast from active toasts
    this.activeToasts = this.activeToasts.filter((toast) => toast !== hiddenToast);

    // Remove the element from the DOM
    if (hiddenToast.parentNode) {
      hiddenToast.parentNode.removeChild(hiddenToast);
    }

    // Process the queue again to show the next toast if space is available
    this.processQueue();
  }

  /**
   * Convenience methods for different toast types
   */
  success(description, header = 'Success!', duration = 5000, target = document.body) {
    this.addToast({
      bgStyle: 'success',
      header: header,
      description: description,
      duration: duration,
      emoji: '✅',
      target: target,
    });
  }

  error(description, header = 'Error!', duration = 7000, target = document.body) {
    this.addToast({
      bgStyle: 'custom',
      header: header,
      description: description,
      duration: duration,
      emoji: '❌',
      target: target,
    });
  }

  info(description, header = 'Info', duration = 5000, target = document.body) {
    this.addToast({
      bgStyle: 'basic',
      header: header,
      description: description,
      duration: duration,
      emoji: 'ℹ️',
      target: target,
    });
  }
}

// Export a singleton instance
const toastManager = new ToastManager();
window.toastManager = toastManager; // Expose globally for easier access in a non-module environment
